import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Collapse from '@mui/material/Collapse';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import CircularProgress from '@mui/material/CircularProgress';
import AgreementTable from './AgreementTable';
import ChangePasswordDialog from './ChangePasswordDialog';
import EditDetailsDialog from './EditDetailsDialog';
import DeleteAccountDialog from './DeleteAccountDialog';
import CancelSubscriptionDialog from './CancelSubscriptionDialog';
import ReactivateSubscriptionDialog from './ReactivateSubscriptionDialog';
import LoadingProgress from '../../register/LoadingProgress';
import ConnectingProgress from '../../register/ConnectingProgress';
import { useTheme } from '@mui/material/styles';
import ApiClient from '../../utils/apiClient';
import { useMediaQuery } from '@mui/material';
import { getCachedData, setCachedData } from '../../utils/cacheUtils';
import HomeIcon from '@mui/icons-material/Home';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import SpeedRoundedIcon from '@mui/icons-material/SpeedRounded';
import UpgradeIcon from '@mui/icons-material/Upgrade';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import Grid from '@mui/material/Grid2';

interface UserInfo {
  first_name: string;
  last_name: string;
  email: string;
  region_id: number;
}

interface Preferences {
  email_new_rates: boolean;
  email_negative_rates: boolean;
  email_cheapest_period: boolean;
  email_expensive_period: boolean;
  email_cleanest_period: boolean;
  email_dirtiest_period: boolean;
  email_monthly_report: boolean;
}

const preferenceDefinitions: Array<{ key: keyof Preferences; label: string }> = [
  { key: 'email_new_rates', label: 'New rates are published for your tariff' },
  // { key: 'email_negative_rates', label: 'Your rates are about to become negative' },
  // { key: 'email_cheapest_period', label: 'The cheapest 2-hour period is approaching' },
  // { key: 'email_expensive_period', label: 'The most expensive 2-hour period is approaching' },
  // { key: 'email_cleanest_period', label: 'The cleanest 2-hour period is approaching' },
  // { key: 'email_dirtiest_period', label: 'The dirtiest 2-hour period is approaching' },
  { key: 'email_monthly_report', label: 'Your monthly report is available' },
];

interface Agreement {
  tariff_code: string;
  product_display_name: string;
  valid_from: string | null;
  valid_to: string | null;
}

interface Meter {
  mpan?: string;
  mprn?: string;
  serial_number: string;
  agreements: Agreement[];
}

interface Property {
  moved_in: string | null;
  moved_out: string | null;
  address_line_1: string | null;
  address_line_2: string | null;
  address_line_3: string | null;
  city: string | null;
  county: string | null;
  postcode: string | null;
  gsp_region_id: string | null;
  region_name: string | null;
  electricity_meters: Meter[];
  gas_meters: Meter[];
}

interface AccountData {
  account_number: string;
  octopus_api_key: string;
  properties: Property[];
}

interface SubscriptionInfo {
  subscription_id: string;
  product_name: string;
  access_level: string;
  start_date: string;
  end_date?: string;
  auto_renew: boolean;
  cost: number;
  renewal_cycle: string;
}

interface AccountDashboardProps {
  onNavigate?: (page: string) => void;
}

export default function AccountDashboard({ onNavigate }: AccountDashboardProps) {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [userInfo, setUserInfo] = React.useState<UserInfo | null>(null);

  const [prefsLoading, setPrefsLoading] = React.useState(true);
  const [originalPreferences, setOriginalPreferences] = React.useState<Preferences | null>(null);  
  const [userPreferences, setUserPreferences] = React.useState<Preferences | null>(null);
  const [accountData, setAccountData] = React.useState<AccountData | null>(null);
  const [subscriptionInfo, setSubscriptionInfo] = React.useState<SubscriptionInfo | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [subscriptionLoading, setSubscriptionLoading] = React.useState(true);
  const [invalidOctopusCredentials, setInvalidOctopusCredentials] = React.useState(false);
  const [expandedProperties, setExpandedProperties] = React.useState<Set<number>>(new Set());
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [editDialogOpen, setEditDialogOpen] = React.useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = React.useState(false);
  const [cancelSubscriptionDialogOpen, setCancelSubscriptionDialogOpen] = React.useState(false);
  const [reactivateSubscriptionDialogOpen, setReactivateSubscriptionDialogOpen] = React.useState(false);

  // Loading progress state variables (from ConnectOctopus.tsx)
  const [showProgress, setShowProgress] = React.useState(false);
  const [showConnecting, setShowConnecting] = React.useState(false);
  const [loadedToDate, setLoadedToDate] = React.useState('');
  const [agreementPercentComplete, setAgreementPercentComplete] = React.useState(0);
  const [consumptionPercentComplete, setConsumptionPercentComplete] = React.useState(0);
  const [tariffName, setTariffName] = React.useState('');
  const [agreementPeriod, setAgreementPeriod] = React.useState('');
  const [showConsumptionProgress, setShowConsumptionProgress] = React.useState(false);
  const [connectionProgressComplete, setConnectionProgressComplete] = React.useState(false);

  // Fetch user info
  React.useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          setLoading(false);
          return;
        }

        // Check cache first
        const cachedData = getCachedData<UserInfo>('user-current');
        
        if (cachedData) {
          // Use cached data
          setUserInfo(cachedData);
        } else {
          // Cache miss - fetch from API
          const data = await ApiClient.get('api/users/current');
          
          // Store in cache
          setCachedData('user-current', data);          
          setUserInfo(data);
        }
      } catch (error) {
        console.error('Error fetching user info:', error);
      }
    };

    const fetchUserPreferences = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          setLoading(false);
          return;
        }

        // Check cache first
        const cachedData = getCachedData<Preferences>('user-preferences');
        
        if (cachedData) {
          // Use cached data
          setUserPreferences(cachedData);
          setOriginalPreferences(cachedData);
        } else {
          // Cache miss - fetch from API
          const data = await ApiClient.get('api/users/preferences');
          
          // Store in cache
          setCachedData('user-preferences', data);          
          setUserPreferences(data);
          setOriginalPreferences(data);
        }
        setPrefsLoading(false);
      } catch (error) {
        console.error('Error fetching user preferences:', error);
        setPrefsLoading(false);
      }
    };

    fetchUserInfo();
    fetchUserPreferences();
  }, []);

  // Fetch account data
  React.useEffect(() => {
    const fetchAccountData = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          setLoading(false);
          return;
        }

        // Check cache first
        const cachedData = getCachedData<{ success: boolean; account: AccountData }>('user-account');
        
        if (cachedData) {
          // Use cached data
          setAccountData(cachedData.account);
          setInvalidOctopusCredentials(!cachedData.success);
          setLoading(false);
        } else {
          // Cache miss - fetch from API
          const data = await ApiClient.get('api/users/account');
          
          // Store in cache
          setCachedData('user-account', data);
          
          setAccountData(data.account);
          setInvalidOctopusCredentials(!data.success);
          setLoading(false);
        }
      } catch (error) {
        console.error('Error fetching account data:', error);
        setLoading(false);
      }
    };

    fetchAccountData();
  }, []);

  // Fetch subscription info
  const fetchSubscriptionInfo = async () => {
    setSubscriptionLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        return;
      }

      const data = await ApiClient.get('api/payments/subscription');
      if (data.success) {
        setSubscriptionInfo(data.subscription);
      }
    } catch (error) {
      if (error instanceof Error && (error as any).status === 404) {
        return;
      }
      console.error('Error fetching subscription info:', error);
    } finally {
      setSubscriptionLoading(false);
    }
  };

  // Refetch account data function
  const fetchAccountData = React.useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setLoading(false);
        return;
      }

      // Fetch from API (don't use cache for fresh data)
      const data = await ApiClient.get('api/users/account');
      
      // Store in cache
      setCachedData('user-account', data);
      
      setAccountData(data.account);
      setInvalidOctopusCredentials(!data.success);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching account data:', error);
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    fetchSubscriptionInfo();
  }, []);

  // Effect for consumption loading - triggers only when connection is complete
  React.useEffect(() => {
    if (connectionProgressComplete) {
      setShowProgress(false);
      setShowConsumptionProgress(true);
      setConsumptionPercentComplete(0); // Reset consumption progress
      setLoadedToDate(''); // Reset loaded to date
      
      const userId: any = localStorage.getItem("user_id");
      const eventSource = new EventSource(`${ApiClient.getCurrentBaseUrl()}/api/connect/load/octopus?id=${userId}`);
      let timeout: NodeJS.Timeout;

      // Function to reset a timeout to close the event source connection after a period of inactivity
      const resetTimeout = () => {
        clearTimeout(timeout);
        timeout = setTimeout(() => {
          console.warn('SSE connection timeout - closing connection');
          eventSource.close();
        }, 120000); 
      };

      // Event handler for receiving messages
      eventSource.onmessage = function(event) {
        resetTimeout();
        const receivedData = JSON.parse(event.data);
        setLoadedToDate(receivedData.loaded_to_date);
        setConsumptionPercentComplete(receivedData.percent_complete);
        if (receivedData.load_complete) {
          clearTimeout(timeout);
          eventSource.close();
          setShowConsumptionProgress(false);
          // Refresh account data
          fetchAccountData();
          // Send completion event to trigger dashboard refresh
          window.dispatchEvent(new CustomEvent('loadingComplete'));
        }
      };

      // Error handler for SSE connection
      eventSource.onerror = function(event) {
        console.error('SSE connection error:', event);
        clearTimeout(timeout);
        eventSource.close();
        setShowConsumptionProgress(false);
      };

      resetTimeout();

      // Cleanup function
      return () => {
        clearTimeout(timeout);
        eventSource.close();
      };
    }
  }, [connectionProgressComplete, fetchAccountData]);

  const handleOctopusCredentialsChanged = React.useCallback(async (apiKey?: string, accountNumber?: string) => {
    // Use provided values or fall back to accountData
    const finalApiKey = apiKey || accountData?.octopus_api_key;
    const finalAccountNumber = accountNumber || accountData?.account_number;

    if (!finalApiKey || !finalAccountNumber) {
      console.error('Missing API key or account number');
      return;
    }

    // Start by showing the negotiating status
    setShowConnecting(true);
    setConnectionProgressComplete(false);

    try {
      // Make POST request to connect
      const response = await fetch(`${ApiClient.getCurrentBaseUrl()}/api/connect/octopus`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem("token")}`
        },
        body: JSON.stringify({
          apiKey: finalApiKey,
          accountNumber: finalAccountNumber
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      setShowConnecting(false);
      setShowProgress(true);

      // Handle SSE stream
      const reader = response.body?.getReader();
      const decoder = new TextDecoder();

      if (reader) {
        while (true) {
          const { value, done } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value);
          const lines = chunk.split('\n');

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const data = JSON.parse(line.slice(6)); // Remove 'data: ' prefix
                
                if (data.connect_complete === true) {
                  setConnectionProgressComplete(true);
                } else {
                  setAgreementPercentComplete(data.percent_complete || 0);
                  setTariffName(data.tariff_name || 'Loading...');
                  setAgreementPeriod(data.agreement_period || 'Processing...');
                }
              } catch (parseError) {
                console.error('Error parsing SSE data:', parseError);
              }
            }
          }
        }
      }

    } catch (error) {
      console.error('Connection error:', error);
      setShowConnecting(false);
      setShowProgress(false);
    }
  }, [accountData]);

  const handleUpgradeClick = () => {
    navigate('/upgrade');
  };


  const handleExpandProperty = (propertyIndex: number) => {
    const newExpanded = new Set(expandedProperties);
    if (newExpanded.has(propertyIndex)) {
      newExpanded.delete(propertyIndex);
    } else {
      newExpanded.add(propertyIndex);
    }
    setExpandedProperties(newExpanded);
  };

  const formatAddress = (property: Property) => {
    const parts = [
      property.address_line_1,
      property.address_line_2,
      property.address_line_3,
      property.city,
      property.county,
      property.postcode
    ].filter(Boolean);
    
    return parts.join(', ');
  };

  const handleOpenDeleteDialog = () => {
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
  };

  const handleOpenCancelSubscriptionDialog = () => {
    setCancelSubscriptionDialogOpen(true);
  };

  const handleCloseCancelSubscriptionDialog = () => {
    setCancelSubscriptionDialogOpen(false);
  };

  const handleOpenReactivateSubscriptionDialog = () => {
    setReactivateSubscriptionDialogOpen(true);
  };

  const handleCloseReactivateSubscriptionDialog = () => {
    setReactivateSubscriptionDialogOpen(false);
  };

  const handleReactivateSubscription = async () => {
    try {
      await ApiClient.post('api/payments/reactivate-subscription');
      setReactivateSubscriptionDialogOpen(false);
      // Refresh subscription info
      await fetchSubscriptionInfo();
    } catch (error) {
      console.error('Error reactivating subscription:', error);
    }
  };

  const handleEditClick = () => {
    setEditDialogOpen(true);
  };

  const handleEditSave = (updatedData: {
    first_name: string;
    last_name: string;
    email: string;
    octopus_account_number: string;
    octopus_api_key: string;
  }) => {
    // Update local state with new data
    if (userInfo) {
      setUserInfo({
        ...userInfo,
        first_name: updatedData.first_name,
        last_name: updatedData.last_name,
        email: updatedData.email
      });
    }
    
    if (accountData) {
      setAccountData({
        ...accountData,
        account_number: updatedData.octopus_account_number || accountData.account_number
      });
    }
  };

  const handlePasswordChangeClick = () => {
    setPasswordDialogOpen(true);
  };

  const prefsChanged = React.useMemo(() => {
    if (!userPreferences || !originalPreferences) {
      return false;
    }

    return (
      userPreferences?.email_new_rates !== originalPreferences?.email_new_rates ||
      userPreferences?.email_negative_rates !== originalPreferences?.email_negative_rates ||
      userPreferences?.email_cheapest_period !== originalPreferences?.email_cheapest_period ||
      userPreferences?.email_expensive_period !== originalPreferences?.email_expensive_period ||
      userPreferences?.email_cleanest_period !== originalPreferences?.email_cleanest_period ||
      userPreferences?.email_dirtiest_period !== originalPreferences?.email_dirtiest_period ||
      userPreferences?.email_monthly_report !== originalPreferences?.email_monthly_report
    );
  }, [userPreferences, originalPreferences]);

  const handleTogglePreference = (key: keyof Preferences) => {
    setUserPreferences((prev) => prev ? { ...prev, [key]: !prev[key] } : prev);
  };

  const handleRevertPreferences = () => {
    setUserPreferences(originalPreferences);
  };

  const handleSavePreferences = async () => {
    if (!userPreferences) {
      return;
    }

    try {
      const updatedPrefs = await ApiClient.put('api/users/preferences', userPreferences);
      if (updatedPrefs && updatedPrefs.success) {
        setOriginalPreferences(userPreferences);
        setCachedData('user-preferences', userPreferences);
      } else {
        throw new Error('Failed to save preferences');
      }
    } catch (error) {
      console.error('Error saving preferences:', error);
    }
  };

  return (
    <Box data-dashboard-id="account">
      <Box sx={{ px: { xs: 1, md: 0 } }}>
        <Typography variant="h1" sx={{ mb: 3, mt: 3 }}>
          Account Information
        </Typography>
      </Box>

        {loading ? (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            minHeight: '400px',
            flexDirection: 'column',
            gap: 2
          }}>
            <CircularProgress />
            <Typography variant="body1" color="text.secondary">
              Loading account information...
            </Typography>
          </Box>
        ) : (
          <>
            {/* User Info Section */}
        <Box sx={{ 
          mb: 3, 
          p: 2, 
          borderRadius: isMobile ? 0 : 1, 
          backgroundColor: theme.palette.background.paper,
          border: 0
        }}>
          <Stack spacing={2}>
            <Typography variant="h2">
              {userInfo ? `${userInfo.first_name} ${userInfo.last_name}` : 'Loading...'}
            </Typography>
            <Typography variant="body1">
              {userInfo?.email || 'Loading...'}
            </Typography>
            {accountData && (
              <>
                <Typography 
                  variant="body1"
                  sx={{ color: invalidOctopusCredentials ? 'error.main' : 'inherit' }}
                >
                  Octopus Account: {accountData.account_number}
                </Typography>
                <Typography 
                  variant="body1"
                  sx={{ color: invalidOctopusCredentials ? 'error.main' : 'inherit' }}
                >
                  Octopus API Key: {accountData.octopus_api_key}
                </Typography>
                {invalidOctopusCredentials && (
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: 'error.main',
                      mt: 1,
                      fontStyle: 'italic'
                    }}
                  >
                    Unable to connect to access your Octopus Energy data. Please click "Edit details" and update your API key and account number.
                  </Typography>
                )}
              </>
            )}
          </Stack>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mt: 4 }}>
            <Stack direction="row" spacing={{ xs: 1, md: 2 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={handleEditClick}
              >
                Edit Details
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={handlePasswordChangeClick}
              >
                Change Password
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={handleOpenDeleteDialog}
                sx={{ 
                  color: 'error.main',
                  borderColor: 'error.main',
                  '&:hover': {                    
                    backgroundColor: 'error.light',
                  },
                  '&.MuiButton-outlined': {
                    borderColor: 'error.main'
                  }
                }}
              >
                Delete Account
              </Button>
            </Stack>
          </Stack>
        </Box>

        {/* Subscription Section */}
        <Box sx={{ 
          mb: 3, 
          p: 2, 
          borderRadius: isMobile ? 0 : 1, 
          backgroundColor: theme.palette.background.paper,
          border: 0,
          position: 'relative'
        }}>
          <Typography variant="h2" sx={{ mb: 2 }}>
            Subscription
          </Typography>
          
          {subscriptionLoading ? (
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center', 
              minHeight: 200,
              position: 'relative'
            }}>
              <CircularProgress />
            </Box>
          ) : subscriptionInfo ? (
            <Stack spacing={2}>
              {subscriptionInfo.access_level === 'free' ? (
                <Typography variant="body1">
                  You're currently using a free account. Upgrade now to gain the benefits of a subscription and dig deeper in to your energy data.
                </Typography>
              ) : subscriptionInfo.access_level === 'failed' ? (
                <Typography variant="body1">
                  Your subscriptions details couldn't be loaded. Please try again later.
                </Typography>
              ) : (
                <>
                  <Typography variant="body1">
                    <strong>Plan:</strong> {subscriptionInfo.product_name} (£{subscriptionInfo.cost.toFixed(2)} per {subscriptionInfo.renewal_cycle})
                  </Typography>                              
                  <Typography variant="body1">
                    <strong>Start Date:</strong> {new Date(subscriptionInfo.start_date).toLocaleDateString()}
                  </Typography>
                </>
              )}                  
              
              {subscriptionInfo.end_date && (
                <Typography variant="body1">
                  {subscriptionInfo.auto_renew 
                    ? `Your plan will automatically renew on ${new Date(subscriptionInfo.end_date).toLocaleDateString()}`
                    : `Your plan will expire on ${new Date(subscriptionInfo.end_date).toLocaleDateString()}`
                  }
                </Typography>
              )}
              
              <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                {subscriptionInfo.access_level === 'free' && (
                  <Button
                    variant="contained"
                    startIcon={<UpgradeIcon />}
                    onClick={handleUpgradeClick}
                    sx={{ backgroundColor: theme.palette.primary.main }}
                  >
                    Upgrade
                  </Button>
                )}
                {subscriptionInfo.access_level !== 'free' && subscriptionInfo.auto_renew && (
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={handleOpenCancelSubscriptionDialog}
                  >
                    Cancel Subscription
                  </Button>
                )}
                {subscriptionInfo.access_level !== 'free' && !subscriptionInfo.auto_renew && (
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={handleOpenReactivateSubscriptionDialog}
                  >
                    Reactivate Subscription
                  </Button>
                )}
              </Stack>
            </Stack>
           ) : (
             <Stack spacing={2}>
               <Typography variant="body1">
                 <strong>Plan:</strong> Free Tier
               </Typography>
               <Typography variant="body1">
                 <strong>Cost:</strong> £0.00
               </Typography>
               
               {/* Only show upgrade button for free tier users */}
               {(() => {
                 const access = localStorage.getItem("access");
                 const isFreeTier = !access || access === 'null' || access === 'free';
                 return isFreeTier ? (
                   <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                     <Button
                       variant="contained"
                       startIcon={<UpgradeIcon />}
                       onClick={handleUpgradeClick}
                       sx={{ backgroundColor: theme.palette.primary.main }}
                     >
                       Upgrade
                     </Button>
                   </Stack>
                 ) : null;
               })()}
             </Stack>
           )}
        </Box>

        {/* Preferences Section */}
        {subscriptionInfo ? (
        <Box sx={{ 
          mb: 3, 
          p: 2, 
          borderRadius: isMobile ? 0 : 1, 
          backgroundColor: theme.palette.background.paper,
          border: 0
        }}>
          <Typography variant="h2" sx={{ mb: 2 }}>
            Alert Preferences
          </Typography>

          {prefsLoading ? (
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center', 
              minHeight: 200,
              position: 'relative'
            }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Typography variant="h5" sx={{ mb: 2 }}>
                Send an e-mail alert when:
              </Typography>
              <FormGroup>
                <Grid container columnSpacing={2} rowSpacing={0.5}>
                  {preferenceDefinitions.map((preference) => (
                    <Grid key={preference.key} size={{ xs: 12, md: 6 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={userPreferences ? userPreferences[preference.key] : false}
                            onChange={() => handleTogglePreference(preference.key)}
                          />
                        }
                        label={preference.label}
                        sx={{ mr: 0, alignItems: 'center' }}
                      />
                    </Grid>
                  ))}
                </Grid>
              </FormGroup>

              <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                <Button
                  variant="contained"
                  onClick={() => handleSavePreferences()}
                  disabled={!prefsChanged || prefsLoading}
                >
                  Save
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => handleRevertPreferences()}
                  disabled={!prefsChanged || prefsLoading}
                >
                  Revert
                </Button>
              </Stack>
            </>
          )}
        </Box>) : null}

        {/* Properties Section */}
        {accountData && accountData.properties.length > 0 ? (
          <Stack spacing={3} sx={{ mb: 3 }}>
            <Box sx={{ px: { xs: 1, md: 0 } }}>
              <Typography variant="h2">
                Your Properties
              </Typography>
            </Box>
            {accountData.properties.map((property, propertyIndex) => (
              <Box 
                 key={propertyIndex} 
                 sx={{
                   p: 2,
                   borderRadius: isMobile ? 0 : 1, 
                   backgroundColor: theme.palette.background.paper,
                   border: 0
                 }}
               >
                <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                  <HomeIcon sx={{ color: 'text.primary' }} />
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="h4">
                      {formatAddress(property)}
                    </Typography>
                    <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
                      <Chip 
                        label={`GSP region: ${property.gsp_region_id || 'N/A'}`} 
                        size="small" 
                        variant="outlined" 
                        sx={{ px: 0.5, py: 1.6 }}
                      />
                      <Chip 
                        label={property.region_name || 'N/A'} 
                        size="small" 
                        variant="outlined" 
                        sx={{ px: 0.5, py: 1.6 }}
                      />
                    </Stack>
                  </Box>
                  <IconButton 
                    onClick={() => handleExpandProperty(propertyIndex)}
                    size="small"
                  >
                  {expandedProperties.has(propertyIndex) ? 
                    <ExpandLessIcon /> : 
                    <ExpandMoreIcon />
                  }
                  </IconButton>
                </Stack>

                <Collapse in={expandedProperties.has(propertyIndex)}>
                  <Divider sx={{ my: 2 }} />
                  
                  {/* Electricity Meters */}
                  {property.electricity_meters.length > 0 && (
                    <Box sx={{ mb: 3, 
                      backgroundColor: theme.palette.background.paper }}>
                      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                        <BoltRoundedIcon sx={{ color: 'text.primary' }} />
                        <Typography variant="h4">
                          Electricity Meters
                        </Typography>
                      </Stack>
                       
                      {property.electricity_meters.map((meter, meterIndex) => (
                        <Box key={meterIndex} sx={{ mb: 2 }}>
                          <Typography variant="h5" sx={{ mb: 1 }} color={theme.palette.mode === 'dark' ? 'text.secondary' : 'text.primary'}>
                            MPAN: {meter.mpan} | Serial: {meter.serial_number}
                          </Typography>                           
                          <AgreementTable 
                            agreements={meter.agreements}
                          />
                        </Box>
                      ))}
                    </Box>
                  )}

                  {/* Gas Meters */}
                  {property.gas_meters.length > 0 && (
                    <Box>
                      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                        <SpeedRoundedIcon sx={{ color: 'text.primary' }} />
                        <Typography variant="h4">
                          Gas Meters
                        </Typography>
                      </Stack>
                       
                      {property.gas_meters.map((meter, meterIndex) => (
                        <Box key={meterIndex} sx={{ mb: 2 }}>
                          <Typography variant="h5" sx={{ mb: 1 }} color={theme.palette.mode === 'dark' ? 'text.secondary' : 'text.primary'}>
                            MPRN: {meter.mprn} | Serial: {meter.serial_number}
                          </Typography>
                          <AgreementTable 
                            agreements={meter.agreements}
                          />
                        </Box>
                       ))}
                    </Box>
                  )}
                </Collapse>
              </Box>
            ))}
          </Stack>
        ) : (
          <Box sx={{ 
             p: 2, 
             borderRadius: isMobile ? 0 : 1, 
             backgroundColor: theme.palette.background.default,
             border: 0
             
          }}>
            <Typography variant="body1" color="text.secondary" align="center">
              No properties found. Please ensure your Octopus account is properly connected.
            </Typography>
          </Box>
        )}
        
        {/* Delete Confirmation Dialog */}
        <DeleteAccountDialog
          open={deleteDialogOpen}
          onClose={handleCloseDeleteDialog}
        />

        {/* Cancel Subscription Confirmation Dialog */}
        <CancelSubscriptionDialog
          open={cancelSubscriptionDialogOpen}
          onClose={handleCloseCancelSubscriptionDialog}
          onSuccess={fetchSubscriptionInfo}
          subscriptionInfo={subscriptionInfo}
        />

        {/* Reactivate Subscription Confirmation Dialog */}
        {subscriptionInfo && (
          <ReactivateSubscriptionDialog
            open={reactivateSubscriptionDialogOpen}
            onClose={handleCloseReactivateSubscriptionDialog}
            onConfirm={handleReactivateSubscription}
            subscriptionInfo={subscriptionInfo}
          />
        )}

        {/* Password Change Dialog */}
        <ChangePasswordDialog 
          open={passwordDialogOpen}
          onClose={() => setPasswordDialogOpen(false)}
        />

        {/* Loading Progress Components */}
        <LoadingProgress 
          open={showProgress} 
          loadedTo={loadedToDate} 
          progress={agreementPercentComplete}
          showAgreementDetails={true}
          tariffName={tariffName}
          agreementPeriod={agreementPeriod}
        />
        <LoadingProgress 
          open={showConsumptionProgress} 
          loadedTo={loadedToDate} 
          progress={consumptionPercentComplete} 
        />
        <ConnectingProgress open={showConnecting} service='Octopus Energy' />

        {/* Edit Details Dialog */}
        <EditDetailsDialog 
          open={editDialogOpen}
          onClose={() => setEditDialogOpen(false)}
          userInfo={userInfo}
          accountData={accountData}
          onSave={handleEditSave}
          onOctopusCredentialsChanged={handleOctopusCredentialsChanged}
        />
          </>
        )}
    </Box>
  );
}
